// RECRUITMENT-CANDIDATE-PROFILE-001 — shared candidate profile fields (form + display)
import React, { useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';

export const CTC_CURRENCIES = ['INR', 'USD', 'AED', 'GBP', 'EUR', 'CAD'];
export const QUALIFICATIONS = [
  ['high_school', 'High School'], ['diploma', 'Diploma'], ['bachelors', "Bachelor's"], ['masters', "Master's"],
  ['doctorate', 'Doctorate'], ['professional_certification', 'Professional Certification'], ['other', 'Other'],
];
export const PROFILE_KEYS = [
  'highest_qualification', 'qualification_detail', 'relevant_experience', 'total_experience',
  'current_location', 'preferred_location', 'willing_to_relocate', 'notice_period_days', 'notice_negotiable',
  'current_ctc', 'expected_ctc', 'ctc_negotiable',
];
const CTC_KEYS = ['current_ctc', 'expected_ctc', 'ctc_negotiable'];

export const EMPTY_PROFILE = {
  highest_qualification: '', qualification_detail: '',
  relevant_experience: { years: '', months: '' }, total_experience: { years: '', months: '' },
  current_location: '', preferred_location: '', willing_to_relocate: 'unset',
  notice_period_days: '', notice_negotiable: 'unset',
  current_ctc: { amount: '', currency: 'INR' }, expected_ctc: { amount: '', currency: 'INR' }, ctc_negotiable: 'unset',
};

const tri = (v) => (v === null || v === undefined ? 'unset' : v ? 'yes' : 'no');
const triOut = (v) => (v === 'yes' ? true : v === 'no' ? false : null);

export const profileFromCandidate = (c = {}) => ({
  highest_qualification: c.highest_qualification || '',
  qualification_detail: c.qualification_detail || '',
  relevant_experience: { years: c.relevant_experience?.years ?? '', months: c.relevant_experience?.months ?? '' },
  total_experience: { years: c.total_experience?.years ?? '', months: c.total_experience?.months ?? '' },
  current_location: c.current_location || '',
  preferred_location: c.preferred_location || '',
  willing_to_relocate: tri(c.willing_to_relocate),
  notice_period_days: c.notice_period_days ?? '',
  notice_negotiable: tri(c.notice_negotiable),
  current_ctc: { amount: c.current_ctc?.amount ?? '', currency: c.current_ctc?.currency || 'INR' },
  expected_ctc: { amount: c.expected_ctc?.amount ?? '', currency: c.expected_ctc?.currency || 'INR' },
  ctc_negotiable: tri(c.ctc_negotiable),
});

const expOut = (e) => (e.years === '' && e.months === '' ? null : { years: Number(e.years || 0), months: Number(e.months || 0) });
const ctcOut = (c) => (c.amount === '' ? null : { amount: Number(c.amount), currency: c.currency, period: 'annual' });

// Payload for POST/PUT — blanks become null (explicit clear). CTC keys omitted when !includeCtc.
export const profileToPayload = (p, includeCtc) => {
  const out = {
    highest_qualification: p.highest_qualification || null,
    qualification_detail: p.qualification_detail.trim() || null,
    relevant_experience: expOut(p.relevant_experience),
    total_experience: expOut(p.total_experience),
    current_location: p.current_location.trim() || null,
    preferred_location: p.preferred_location.trim() || null,
    willing_to_relocate: triOut(p.willing_to_relocate),
    notice_period_days: p.notice_period_days === '' ? null : Number(p.notice_period_days),
    notice_negotiable: triOut(p.notice_negotiable),
  };
  if (includeCtc) {
    out.current_ctc = ctcOut(p.current_ctc);
    out.expected_ctc = ctcOut(p.expected_ctc);
    out.ctc_negotiable = triOut(p.ctc_negotiable);
  }
  return out;
};

export const validateProfile = (p) => {
  const r = expOut(p.relevant_experience), t = expOut(p.total_experience);
  if (r && t && r.years * 12 + r.months > t.years * 12 + t.months) return 'Relevant experience cannot exceed total experience';
  return null;
};

// ---- display helpers
export const fmtExperience = (e) => (e ? `${e.years || 0} yr${e.years === 1 ? '' : 's'} ${e.months || 0} mo${e.months === 1 ? '' : 's'}` : '—');
export const fmtTri = (v) => (v === null || v === undefined ? 'Not specified' : v ? 'Yes' : 'No');
export const fmtNotice = (d) => (d === null || d === undefined ? '—' : d === 0 ? 'Immediate' : `${d} day${d === 1 ? '' : 's'}`);
export const fmtQualification = (q, detail) => {
  const label = QUALIFICATIONS.find(([k]) => k === q)?.[1];
  if (!label) return detail || '—';
  return detail ? `${label} · ${detail}` : label;
};
export const fmtCtc = (c) => {
  if (!c || c.amount === null || c.amount === undefined) return '—';
  try {
    return `${new Intl.NumberFormat(c.currency === 'INR' ? 'en-IN' : 'en-US', { style: 'currency', currency: c.currency, maximumFractionDigits: 0 }).format(c.amount)} / yr`;
  } catch { return `${c.amount} ${c.currency} / yr`; }
};

export const TriSelect = ({ value, onChange, testId }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger className="h-9" data-testid={testId}><SelectValue /></SelectTrigger>
    <SelectContent>
      <SelectItem value="unset">Not specified</SelectItem>
      <SelectItem value="yes">Yes</SelectItem>
      <SelectItem value="no">No</SelectItem>
    </SelectContent>
  </Select>
);

export const ExpInput = ({ value, onChange, testId }) => (
  <div className="flex gap-2">
    <Input type="number" min={0} max={50} placeholder="Years" value={value.years} className="h-9"
      onChange={(e) => onChange({ ...value, years: e.target.value })} data-testid={`${testId}-years`} />
    <Input type="number" min={0} max={11} placeholder="Months" value={value.months} className="h-9"
      onChange={(e) => onChange({ ...value, months: e.target.value })} data-testid={`${testId}-months`} />
  </div>
);

export const CtcInput = ({ value, onChange, testId }) => (
  <div className="flex gap-2">
    <Input type="number" min={0} step="1000" placeholder="Amount (annual)" value={value.amount} className="h-9 flex-1"
      onChange={(e) => onChange({ ...value, amount: e.target.value })} data-testid={`${testId}-amount`} />
    <Select value={value.currency} onValueChange={(v) => onChange({ ...value, currency: v })}>
      <SelectTrigger className="h-9 w-[92px]" data-testid={`${testId}-currency`}><SelectValue /></SelectTrigger>
      <SelectContent>{CTC_CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
    </Select>
  </div>
);

const F = ({ label, children }) => (<div className="space-y-1"><Label className="text-xs text-slate-600">{label}</Label>{children}</div>);
const Section = ({ title, children }) => (
  <div className="space-y-3">
    <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{title}</p>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">{children}</div>
  </div>
);

export const CandidateProfileFields = ({ value, onChange, showCtc }) => {
  const set = (k, v) => onChange({ ...value, [k]: k === 'highest_qualification' && v === 'none' ? '' : v });
  return (
    <div className="space-y-5" data-testid="candidate-profile-fields">
      <Section title="Personal / Location">
        <F label="Current Location"><Input className="h-9" value={value.current_location} onChange={(e) => set('current_location', e.target.value)} data-testid="profile-current-location" /></F>
        <F label="Preferred Location"><Input className="h-9" value={value.preferred_location} onChange={(e) => set('preferred_location', e.target.value)} data-testid="profile-preferred-location" /></F>
        <F label="Willing to Relocate"><TriSelect value={value.willing_to_relocate} onChange={(v) => set('willing_to_relocate', v)} testId="profile-willing-to-relocate" /></F>
      </Section>
      <Section title="Education & Experience">
        <F label="Highest Qualification">
          <Select value={value.highest_qualification || 'none'} onValueChange={(v) => set('highest_qualification', v)}>
            <SelectTrigger className="h-9" data-testid="profile-highest-qualification"><SelectValue placeholder="Select" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">Not specified</SelectItem>
              {QUALIFICATIONS.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
            </SelectContent>
          </Select>
        </F>
        <F label="Qualification Detail"><Input className="h-9" placeholder="e.g. B.Com, MBA (Finance)" value={value.qualification_detail} onChange={(e) => set('qualification_detail', e.target.value)} data-testid="profile-qualification-detail" /></F>
        <F label="Relevant Work Experience"><ExpInput value={value.relevant_experience} onChange={(v) => set('relevant_experience', v)} testId="profile-relevant-exp" /></F>
        <F label="Total Work Experience"><ExpInput value={value.total_experience} onChange={(v) => set('total_experience', v)} testId="profile-total-exp" /></F>
      </Section>
      <Section title={showCtc ? 'Compensation & Availability' : 'Availability'}>
        <F label="Notice Period (days, 0 = Immediate)"><Input type="number" min={0} max={365} className="h-9" value={value.notice_period_days} onChange={(e) => set('notice_period_days', e.target.value)} data-testid="profile-notice-period" /></F>
        <F label="Notice Period Negotiable"><TriSelect value={value.notice_negotiable} onChange={(v) => set('notice_negotiable', v)} testId="profile-notice-negotiable" /></F>
        {showCtc && (
          <>
            <F label="Current CTC (annual)"><CtcInput value={value.current_ctc} onChange={(v) => set('current_ctc', v)} testId="profile-current-ctc" /></F>
            <F label="Expected CTC (annual)"><CtcInput value={value.expected_ctc} onChange={(v) => set('expected_ctc', v)} testId="profile-expected-ctc" /></F>
            <F label="CTC Negotiable"><TriSelect value={value.ctc_negotiable} onChange={(v) => set('ctc_negotiable', v)} testId="profile-ctc-negotiable" /></F>
          </>
        )}
      </Section>
    </div>
  );
};

// Collapsible wrapper for the Add Candidate dialogs (optional details)
export const OptionalProfileSection = ({ value, onChange, showCtc }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-slate-200 rounded-lg">
      <button type="button" onClick={() => setOpen(!open)} data-testid="toggle-profile-details"
        className="w-full flex items-center justify-between px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 rounded-lg">
        <span>Profile details (optional)</span>
        {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
      </button>
      {open && <div className="p-3 border-t border-slate-100"><CandidateProfileFields value={value} onChange={onChange} showCtc={showCtc} /></div>}
    </div>
  );
};

export const hasCtcKeys = (c) => CTC_KEYS.some((k) => k in (c || {}));
